<template>
  <div>
    <!-- Barra superior -->
    <TopBar />

    <!-- Breadcrumb del flujo de producción -->
    <ProductionBreadcrumb
      :current-view="currentView"
      :tipo-seleccionado="tipoSeleccionado"
      :fruit-type="frutaSeleccionada"
      :finca="fincaSeleccionada"
      :lomo="lomoSeleccionado"
      :variedad="variedadSeleccionada"
      :zone-step="zoneStep"
      :zona="zonaSeleccionada"
      :formato="formatoSeleccionado"
      :modo="modoProduccionObj || modoProduccion"
      :cooperative-step="cooperativeStep"
      :categoria="categoriaObj || categoriaSeleccionada"
      :count-widget-step="countWidgetStep"
      @navigate-back="handleBreadcrumbBack"
    />

    <!-- Barra lateral que puedes abrir/cerrar -->
    <BarraLateral
      :isOpen="isBarraVisible"
      @close="isBarraVisible = false"
      @navigate="handleNavigation"
    />

    <!-- Contenedor para la vista actual -->
    <div
      class="view-container"
      :class="{ 'with-breadcrumb': currentView >= 2 && currentView <= 4 }"
    >
      <transition name="slide" mode="out-in">
        <component
          :is="currentComponent"
          :key="componentKey"
          v-bind="currentProps"
          @select-zone="handlePredictionZoneSelect"
          @back-to-farm="handlePredictionBackToFarm"
        />
      </transition>
    </div>

    <!-- Barra inferior de navegación -->
    <BottomBar
      :farm-selected="!!fincaSeleccionada"
      :zone-selected="!!zonaSeleccionada"
      :is-staff="isStaff"
      @navigate="handleNavigation"
    />

    <!-- Diálogo de error para finca sin zonas -->
    <dialog ref="errorDialog" class="error-dialog">
      <img :src="errorIcon" alt="Error" class="error-icon" />
      <h2 class="dialog-title">{{ errorMessage }}</h2>
      <div class="dialog-buttons">
        <button @click="closeErrorDialog" class="ok-button">Aceptar</button>
      </div>
    </dialog>

    <!-- Diálogo previo: dato duplicado -->
    <dialog ref="duplicateWarningDialog" class="error-dialog">
      <img :src="errorIcon" alt="Advertencia" class="error-icon" />
      <h2 class="dialog-title">Dato duplicado</h2>
      <p class="duplicate-warning-text">
        ⚠️ Ya existe un registro hoy con la misma Finca, Zona y Categoría.
        ¿Deseas continuar de todas formas?
      </p>
      <div class="dialog-buttons">
        <button @click="goBackFromDuplicateWarning" class="back-button">
          Volver
        </button>
        <button @click="proceedFromDuplicateWarning" class="ok-button">
          Avanzar
        </button>
      </div>
    </dialog>

    <QRZoneScannerModal
      ref="qrScannerModal"
      :visible="isQrScannerOpen"
      @close="closeQrScanner"
      @scan="handleQrScan"
    />
  </div>
</template>

<script>
import errorIcon from "@/assets/icon/advertencia.png";
import BottomBar from "../widgets/BottomBar.vue";
import FruitCountWidget from "../widgets/FruitCountWidget.vue";
import BarraLateral from "../widgets/ListBarraLateral.vue";
import ListCategories from "../widgets/ListCategories.vue";
import ListFarmlands from "../widgets/ListFarmlands.vue";
import ListFormats from "../widgets/ListFormats.vue";
import ListLomos from "../widgets/ListLomos.vue";
import ListProductionCampaign from "../widgets/ListProductionCampaign.vue";
import ListProductionControl from "../widgets/ListProductionControl.vue";
import ListProductions from "../widgets/ListProductions.vue";
import ListProductionWeekly from "../widgets/ListProductionWeekly.vue";
import ListVarieties from "../widgets/ListVarieties.vue";
import ListZones from "../widgets/ListZones.vue";
import LoteWidget from "../widgets/LoteWidget.vue";
import PredictionsWidget from "../widgets/PredictionsWidget.vue";
import FarmPredictionsWidget from "../widgets/FarmPredictionsWidget.vue";
import ProductionBreadcrumb from "../widgets/ProductionBreadcrumb.vue";
import QRZoneScannerModal from "../widgets/QRZoneScannerModal.vue";
import QuantityWidget from "../widgets/QuantityWidget.vue";
import SelectFarmType from "../widgets/SelectFarmType.vue";
import SelectFruitType from "../widgets/SelectFruitType.vue";
import SelectProductionMode from "../widgets/SelectProductionMode.vue";
import TopBar from "../widgets/TopBar.vue";

export default {
  name: "ProductionView",

  components: {
    TopBar,
    ProductionBreadcrumb,
    BottomBar,
    BarraLateral,
    ListFarmlands,
    ListLomos,
    ListVarieties,
    ListZones,
    ListFormats,
    QuantityWidget,
    ListProductions,
    PredictionsWidget,
    FarmPredictionsWidget,
    ListProductionWeekly,
    ListProductionCampaign,
    ListProductionControl,
    SelectFarmType,
    SelectFruitType,
    SelectProductionMode,
    ListCategories,
    FruitCountWidget,
    LoteWidget,
    QRZoneScannerModal,
  },

  data() {
    return {
      currentView: 1,
      isBarraVisible: false,
      // Paso previo: tipo de finca
      tipoSeleccionado: null, // 'COOPERATIVE' | 'COMMERCIAL'
      farmTypeStep: "type", // 'type' | 'fruit' | 'farmland'
      frutaSeleccionada: null,
      fincaSeleccionada: null,
      lomoSeleccionado: null,
      variedadSeleccionada: null,
      zonaSeleccionada: null,
      formatoSeleccionado: null,
      modoProduccion: null,
      modoProduccionObj: null,
      categoriaSeleccionada: null,
      categoriaObj: null,
      countWidgetStep: null,
      // Sub-paso dentro de vista 2 para invernaderos con lomos:
      // 'lomo' -> 'zone'
      zoneStep: "zone",
      // Sub-paso dentro de vista 3 para cooperativas:
      // 'mode' -> 'category' -> 'format' (solo peso)
      cooperativeStep: "mode",
      errorMessage: "",
      errorIcon,
      pendingCategoryNav: null,
      isQrScannerOpen: false,
      pendingFarmTypeSelectionId: 0,
      predictionZoneId: null,
      predictionOpenedWithZone: false,
    };
  },

  computed: {
    isStaff() {
      const token = localStorage.getItem("token");
      if (!token) return false;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.is_staff === true;
      } catch {
        return false;
      }
    },

    componentKey() {
      const predictionKey =
        this.currentView === 6 ? `-pred-${this.predictionZoneId || "farm"}` : "";
      return `${this.currentView}-${this.zoneStep}-${this.cooperativeStep}-${this.farmTypeStep}${predictionKey}`;
    },

    predictionZone() {
      if (!this.predictionZoneId) return null;
      if (this.zonaSeleccionada?.id === this.predictionZoneId) {
        return this.zonaSeleccionada;
      }
      const zones = this.fincaSeleccionada?.zones;
      if (!Array.isArray(zones)) return null;
      return zones.find((zone) => zone.id === this.predictionZoneId) || null;
    },

    currentComponent() {
      switch (this.currentView) {
        case 1:
          if (this.farmTypeStep === "type") return "SelectFarmType";
          if (this.farmTypeStep === "fruit") return "SelectFruitType";
          return "ListFarmlands";
        case 2:
          // Sub-paso: selección de lomo o variedad antes de zona
          if (this.zoneStep === "lomo") return "ListLomos";
          if (this.zoneStep === "variety") return "ListVarieties";
          return "ListZones";
        case 3:
          // Para comerciales sin zona: seleccionar modo (COUNT) antes de continuar
          if (
            this.fincaSeleccionada?.farm_type === "COMMERCIAL" &&
            !this.zonaSeleccionada &&
            !this.modoProduccion
          ) {
            return "SelectProductionMode";
          }
          // Para comerciales con zona: directo a formatos
          if (
            this.fincaSeleccionada?.farm_type === "COMMERCIAL" &&
            this.zonaSeleccionada
          ) {
            return "ListFormats";
          }
          // Para comerciales sin zona (con modo autoseleccionado) y cooperativas: flujo modo → categoría → formato
          if (this.cooperativeStep === "mode") {
            return "SelectProductionMode";
          } else if (this.cooperativeStep === "category") {
            return "ListCategories";
          } else if (
            this.cooperativeStep === "format" &&
            this.modoProduccionObj?.requires_format
          ) {
            return "ListFormats";
          }
          return "ListFormats"; // Fallback
        case 4: {
          // Cantidad — driven by mode properties (applies to both COMMERCIAL and COOPERATIVE)
          const mo = this.modoProduccionObj;
          if (mo?.requires_fruit_count) {
            // LOTE: multi-step tray estimation
            if (this.modoProduccion === "LOTE") return "LoteWidget";
            // COUNT (or any mode requiring fruit count)
            return "FruitCountWidget";
          }
          // Default: QuantityWidget for modes requiring format/weight
          return "QuantityWidget";
        }
        case 5:
          return "ListProductions";
        case 6:
          return this.predictionZoneId ? "PredictionsWidget" : "FarmPredictionsWidget";
        case 7:
          return "ListProductionWeekly";
        case 8:
          return "ListProductionCampaign";
        case 9:
          return "ListProductionControl";
        default:
          return "ListFarmlands";
      }
    },

    currentProps() {
      const props = {};

      if (this.currentView === 1 && this.farmTypeStep === "farmland") {
        props.farmType = this.tipoSeleccionado;
        props.fruitType = this.frutaSeleccionada;
      }

      if (this.currentView === 2) {
        props.finca = this.fincaSeleccionada;
        if (this.zoneStep === "zone" && this.lomoSeleccionado !== null) {
          props.lomo = this.lomoSeleccionado;
        }
        if (this.zoneStep === "zone" && this.variedadSeleccionada !== null) {
          props.variety = this.variedadSeleccionada;
        }
      }

      if (this.currentView === 3) {
        props.finca = this.fincaSeleccionada;
        props.zona = this.zonaSeleccionada;
        if (this.cooperativeStep === "category") {
          props.mode = this.modoProduccion;
        }
      }

      if (this.currentView === 4) {
        props.farmland = this.fincaSeleccionada;
        props.zone = this.zonaSeleccionada;
        props.format = this.formatoSeleccionado;
        props.modoObj = this.modoProduccionObj;
        props.categoriaObj = this.categoriaObj;
        if (this.categoriaSeleccionada) {
          props.category = this.categoriaSeleccionada;
        }
      }

      if (this.currentView === 6) {
        props.finca = this.fincaSeleccionada;
        if (this.predictionZoneId) {
          props.zona = this.predictionZone;
          props.showBackToFarm = true;
        }
      }

      return props;
    },
  },

  mounted() {
    // Guardar referencias para poder eliminarlos en beforeDestroy
    this._onFarmType = async (tipo) => {
      this.tipoSeleccionado = tipo;
      this.frutaSeleccionada = null;

      if (tipo !== "COOPERATIVE") {
        this.farmTypeStep = "farmland";
        return;
      }

      this.farmTypeStep = "fruit";

      const selectionId = ++this.pendingFarmTypeSelectionId;
      const fruitType = await this.resolveUniqueCooperativeFruitType();

      if (
        selectionId !== this.pendingFarmTypeSelectionId ||
        this.tipoSeleccionado !== "COOPERATIVE" ||
        this.farmTypeStep !== "fruit"
      ) {
        return;
      }

      if (fruitType) {
        this.frutaSeleccionada = fruitType;
        this.farmTypeStep = "farmland";
      }
    };
    this._onFruit = (fruitType) => {
      this.frutaSeleccionada = fruitType;
      this.farmTypeStep = "farmland";
    };
    this._onFarmland = (finca) => {
      this.fincaSeleccionada = finca;
      this.lomoSeleccionado = null;
      this.variedadSeleccionada = null;
      this.zonaSeleccionada = null;
      this.predictionZoneId = null;
      this.predictionOpenedWithZone = false;
      this.formatoSeleccionado = null;
      this.modoProduccion = null;
      this.modoProduccionObj = null;
      this.categoriaSeleccionada = null;
      this.categoriaObj = null;
      this.countWidgetStep = null;
      this.cooperativeStep = "mode";

      const zones = Array.isArray(finca?.zones) ? finca.zones : [];
      if (zones.length === 0) {
        if (finca.farm_type === "COMMERCIAL") {
          // Las fincas comerciales pueden registrar producción sin zona.
          // Pasan por SelectProductionMode para COUNT, luego a formatos.
          this.cooperativeStep = "mode";
          this.setView(3);
          return;
        }

        this.errorMessage = `La finca "${finca.name}" no tiene zonas asociadas. Por favor, configure las zonas antes de continuar.`;
        this.$refs.errorDialog.showModal();
        return;
      }

      // Prioridad: lomos → variedades → directo a zona
      const hasLomos = zones.some(
        (z) => z.lomo !== null && z.lomo !== undefined,
      );
      const hasVarieties = zones.some(
        (z) =>
          z.variety !== null && z.variety !== undefined && z.variety !== "",
      );
      const uniqueVariety = this.getUniqueZoneVariety(zones);
      if (hasLomos) this.zoneStep = "lomo";
      else if (uniqueVariety) {
        this.variedadSeleccionada = uniqueVariety;
        this.zoneStep = "zone";
      } else if (hasVarieties) {
        this.zoneStep = "variety";
      } else {
        this.zoneStep = "zone";
      }
      this.setView(2);
    };
    this._onLomo = (lomo) => {
      this.lomoSeleccionado = lomo;
      this.zoneStep = "zone";
      // No cambiamos de vista, solo actualizamos el sub-paso
    };
    this._onVariedad = (variedad) => {
      this.variedadSeleccionada = variedad;
      this.zoneStep = "zone";
      // No cambiamos de vista, solo actualizamos el sub-paso
    };
    this._onZone = (zona) => {
      this.zonaSeleccionada = zona;
      this.cooperativeStep = "mode";
      this.setView(3);
    };
    this._onFormat = (formato) => {
      this.formatoSeleccionado = formato;
      this.countWidgetStep = null;
      this.setView(4);
    };
    this._onProductionMode = (modo) => {
      this.modoProduccionObj = modo;
      this.modoProduccion = modo.code || modo;
      // Both COMMERCIAL and COOPERATIVE continue to category selection
      this.cooperativeStep = "category";
    };
    this._onCategory = async (categoria) => {
      this.categoriaObj = categoria;
      this.categoriaSeleccionada = categoria.code || categoria;
      this.countWidgetStep = null;
      const requiresFormat = this.modoProduccionObj?.requires_format;

      const proceed = () => {
        if (requiresFormat) {
          this.cooperativeStep = "format";
        } else {
          this.setView(4);
        }
      };

      if (
        this.zonaSeleccionada?.id &&
        this.fincaSeleccionada?.id &&
        categoria.code
      ) {
        try {
          const params = new URLSearchParams({
            farmland_id: this.fincaSeleccionada.id,
            zone_id: this.zonaSeleccionada.id,
            category: categoria.code,
          });
          const response = await this.$http.get(
            `${this.$hostname}/production/check-duplicate/?${params}`,
          );
          if (response.data?.duplicate) {
            this.pendingCategoryNav = proceed;
            this.$refs.duplicateWarningDialog?.showModal();
            return;
          }
        } catch (err) {
          console.error("Error al verificar duplicado:", err);
        }
      }

      proceed();
    };
    this._onPrediction = () => {
      this.tipoSeleccionado = null;
      this.farmTypeStep = "type";
      this.frutaSeleccionada = null;
      this.pendingFarmTypeSelectionId += 1;
      this.fincaSeleccionada = null;
      this.lomoSeleccionado = null;
      this.variedadSeleccionada = null;
      this.zoneStep = "zone";
      this.zonaSeleccionada = null;
      this.formatoSeleccionado = null;
      this.modoProduccion = null;
      this.modoProduccionObj = null;
      this.categoriaSeleccionada = null;
      this.categoriaObj = null;
      this.cooperativeStep = "mode";
      this.countWidgetStep = null;
      this.setView(6);
    };
    this._onQuantity = () => {
      this.countWidgetStep = null;
      this.tipoSeleccionado = null;
      this.farmTypeStep = "type";
      this.frutaSeleccionada = null;
      this.pendingFarmTypeSelectionId += 1;
      this.setView(1);
    };
    this._onGotoFormatsMode = () => {
      this.countWidgetStep = null;
      this.cooperativeStep = "mode";
      this.setView(3);
    };
    this._onGotoFormatsCategory = () => {
      this.countWidgetStep = null;
      this.cooperativeStep = "category";
      this.setView(3);
    };
    this._onCountStepTitle = (title) => {
      this.countWidgetStep = title;
    };
    this._onToggleBarra = () => {
      this.isBarraVisible = !this.isBarraVisible;
    };
    this._onOpenQrScanner = () => {
      this.isQrScannerOpen = true;
    };

    this.$root.$on("farm-type", this._onFarmType);
    this.$root.$on("farm-fruit", this._onFruit);
    this.$root.$on("farmland", this._onFarmland);
    this.$root.$on("lomo", this._onLomo);
    this.$root.$on("variedad", this._onVariedad);
    this.$root.$on("zone", this._onZone);
    this.$root.$on("format", this._onFormat);
    this.$root.$on("production-mode", this._onProductionMode);
    this.$root.$on("category", this._onCategory);
    this.$root.$on("prediction", this._onPrediction);
    this.$root.$on("quantity", this._onQuantity);
    this.$root.$on("goto-formats-mode", this._onGotoFormatsMode);
    this.$root.$on("goto-formats-category", this._onGotoFormatsCategory);
    this.$root.$on("count-step-title", this._onCountStepTitle);
    this.$root.$on("toggle-barra", this._onToggleBarra);
    this.$root.$on("open-qr-zone-scanner", this._onOpenQrScanner);

    // Sincroniza la vista interna con la ruta al cargar y al navegar
    this.syncViewWithRoute(this.$route);
    this.$watch("$route", (to) => {
      this.syncViewWithRoute(to);
    });
  },

  beforeDestroy() {
    this.$root.$off("farm-type", this._onFarmType);
    this.$root.$off("farm-fruit", this._onFruit);
    this.$root.$off("farmland", this._onFarmland);
    this.$root.$off("lomo", this._onLomo);
    this.$root.$off("variedad", this._onVariedad);
    this.$root.$off("zone", this._onZone);
    this.$root.$off("format", this._onFormat);
    this.$root.$off("production-mode", this._onProductionMode);
    this.$root.$off("category", this._onCategory);
    this.$root.$off("prediction", this._onPrediction);
    this.$root.$off("quantity", this._onQuantity);
    this.$root.$off("goto-formats-mode", this._onGotoFormatsMode);
    this.$root.$off("goto-formats-category", this._onGotoFormatsCategory);
    this.$root.$off("count-step-title", this._onCountStepTitle);
    this.$root.$off("toggle-barra", this._onToggleBarra);
    this.$root.$off("open-qr-zone-scanner", this._onOpenQrScanner);
  },

  methods: {
    async resolveUniqueCooperativeFruitType() {
      try {
        const response = await this.$http.get(`${this.$hostname}/farmlands/`);
        const farmlands = Array.isArray(response?.data) ? response.data : [];
        const fruitTypes = new Set(
          farmlands
            .filter((farmland) => farmland?.farm_type === "COOPERATIVE")
            .map((farmland) => String(farmland?.fruit_type || "").trim())
            .filter(Boolean),
        );

        return fruitTypes.size === 1 ? [...fruitTypes][0] : null;
      } catch (error) {
        console.error(
          "Error al resolver el fruit type único de las fincas:",
          error,
        );
        return null;
      }
    },

    getUniqueZoneVariety(zones) {
      if (!Array.isArray(zones) || zones.length === 0) return null;

      const normalizedVarieties = zones
        .map((zone) => String(zone?.variety || "").trim())
        .filter(Boolean);

      if (normalizedVarieties.length !== zones.length) return null;

      const uniqueVarieties = new Set(normalizedVarieties);
      return uniqueVarieties.size === 1 ? normalizedVarieties[0] : null;
    },

    handleNavigation(view) {
      // Resetear estado cuando se navega directamente a vistas de listado
      if (view === 1) {
        // Siempre volver al paso de selección de tipo
        this.tipoSeleccionado = null;
        this.farmTypeStep = "type";
        this.frutaSeleccionada = null;
        this.pendingFarmTypeSelectionId += 1;
        this.fincaSeleccionada = null;
        this.lomoSeleccionado = null;
        this.variedadSeleccionada = null;
        this.zoneStep = "zone";
        this.zonaSeleccionada = null;
        this.formatoSeleccionado = null;
        this.modoProduccion = null;
        this.modoProduccionObj = null;
        this.categoriaSeleccionada = null;
        this.categoriaObj = null;
        this.cooperativeStep = "mode";
        this.countWidgetStep = null;
      } else if (view === 5 || view === 7 || view === 8 || view === 9) {
        this.tipoSeleccionado = null;
        this.farmTypeStep = "type";
        this.frutaSeleccionada = null;
        this.pendingFarmTypeSelectionId += 1;
        this.fincaSeleccionada = null;
        this.lomoSeleccionado = null;
        this.variedadSeleccionada = null;
        this.zoneStep = "zone";
        this.zonaSeleccionada = null;
        this.formatoSeleccionado = null;
        this.modoProduccion = null;
        this.modoProduccionObj = null;
        this.categoriaSeleccionada = null;
        this.categoriaObj = null;
        this.cooperativeStep = "mode";
        this.countWidgetStep = null;
      }

      if (view === 6) {
        this.syncPredictionEntryState();
      } else {
        this.predictionZoneId = null;
        this.predictionOpenedWithZone = false;
      }

      this.setView(view);
      this.isBarraVisible = false;
    },

    syncPredictionEntryState() {
      this.predictionOpenedWithZone = !!this.zonaSeleccionada;
      this.predictionZoneId = this.zonaSeleccionada?.id ?? null;
    },

    handlePredictionZoneSelect(zone) {
      if (!zone?.id) return;
      this.predictionZoneId = zone.id;
      this.zonaSeleccionada = zone;
    },

    handlePredictionBackToFarm() {
      this.predictionZoneId = null;
      if (!this.predictionOpenedWithZone) {
        this.zonaSeleccionada = null;
      }
    },

    handleBreadcrumbBack(target) {
      if (!target) return;
      const { view, zoneStep, cooperativeStep } = target;

      // Limpiar estado según el paso al que volvemos
      if (view === 1) {
        const targetFarmTypeStep = target?.farmTypeStep || "farmland";
        if (targetFarmTypeStep === "type") {
          // Volver a la selección de tipo: reset completo
          this.tipoSeleccionado = null;
          this.frutaSeleccionada = null;
          this.farmTypeStep = "type";
          this.pendingFarmTypeSelectionId += 1;
        } else if (targetFarmTypeStep === "fruit") {
          this.frutaSeleccionada = null;
          this.farmTypeStep = "fruit";
        } else {
          // Volver a la lista de fincas conservando el tipo
          this.farmTypeStep = "farmland";
        }
        this.fincaSeleccionada = null;
        this.lomoSeleccionado = null;
        this.variedadSeleccionada = null;
        this.zoneStep = "zone";
        this.zonaSeleccionada = null;
        this.formatoSeleccionado = null;
        this.modoProduccion = null;
        this.modoProduccionObj = null;
        this.categoriaSeleccionada = null;
        this.categoriaObj = null;
        this.cooperativeStep = "mode";
        this.countWidgetStep = null;
      } else if (view === 2) {
        this.zonaSeleccionada = null;
        this.formatoSeleccionado = null;
        this.modoProduccion = null;
        this.modoProduccionObj = null;
        this.categoriaSeleccionada = null;
        this.categoriaObj = null;
        this.cooperativeStep = "mode";
        this.countWidgetStep = null;
        // Si hay sub-paso de lomo o variedad, determinar a cuál volver
        if (zoneStep === "lomo") {
          this.lomoSeleccionado = null;
          this.zoneStep = "lomo";
        } else if (zoneStep === "variety") {
          this.variedadSeleccionada = null;
          this.zoneStep = "variety";
        } else {
          // Volver a la selección de zona (mantener filtro si existe)
          this.zoneStep = "zone";
        }
      } else if (view === 3) {
        this.formatoSeleccionado = null;
        this.countWidgetStep = null;
        if (cooperativeStep === "mode") {
          this.modoProduccion = null;
          this.modoProduccionObj = null;
          this.categoriaSeleccionada = null;
          this.categoriaObj = null;
          this.cooperativeStep = "mode";
        } else if (cooperativeStep === "category") {
          this.categoriaSeleccionada = null;
          this.categoriaObj = null;
          this.cooperativeStep = "category";
        } else if (cooperativeStep === "format") {
          this.cooperativeStep = "format";
        } else {
          // commercial
          this.cooperativeStep = "mode";
        }
      }

      this.setView(view);
    },

    setView(view) {
      if (this.currentView !== view) {
        this.currentView = view;
        this.$root.$emit("change", view);
        // Sincroniza la ruta con la vista
        switch (view) {
          case 1:
            if (this.$route.path !== "/production")
              this.$router.push("/production");
            break;
          case 2:
            if (this.$route.path !== "/production/zones")
              this.$router.push("/production/zones");
            break;
          case 3:
            if (this.$route.path !== "/production/formats")
              this.$router.push("/production/formats");
            break;
          case 4:
            if (this.$route.path !== "/production/quantity")
              this.$router.push("/production/quantity");
            break;
          case 5:
            if (this.$route.path !== "/production/list")
              this.$router.push("/production/list");
            break;
          case 6:
            if (this.$route.path !== "/production/prediction")
              this.$router.push("/production/prediction");
            break;
          case 7:
            if (this.$route.path !== "/production/weekly")
              this.$router.push("/production/weekly");
            break;
          case 8:
            if (this.$route.path !== "/production/campaign")
              this.$router.push("/production/campaign");
            break;
          case 9:
            if (this.$route.path !== "/production/control")
              this.$router.push("/production/control");
            break;
          default:
            if (this.$route.path !== "/production")
              this.$router.push("/production");
        }
      }
    },

    syncViewWithRoute(route) {
      const path = route.path;

      if (path === "/production/prediction") {
        if (!this.isStaff || !this.fincaSeleccionada) {
          if (this.$route.path !== "/production") {
            this.$router.replace("/production");
          }
          this.currentView = 1;
          this.$root.$emit("change", this.currentView);
          return;
        }
        this.syncPredictionEntryState();
        this.currentView = 6;
        this.$root.$emit("change", this.currentView);
        return;
      }

      if (path === "/production") this.currentView = 1;
      else if (path === "/production/zones") this.currentView = 2;
      else if (path === "/production/formats") this.currentView = 3;
      else if (path === "/production/quantity") this.currentView = 4;
      else if (path === "/production/list") this.currentView = 5;
      else if (path === "/production/weekly") this.currentView = 7;
      else if (path === "/production/campaign") this.currentView = 8;
      else if (path === "/production/control") this.currentView = 9;
      else this.currentView = 1;
      this.$root.$emit("change", this.currentView);
    },

    proceedFromDuplicateWarning() {
      this.$refs.duplicateWarningDialog?.close();
      if (this.pendingCategoryNav) {
        this.pendingCategoryNav();
        this.pendingCategoryNav = null;
      }
    },

    goBackFromDuplicateWarning() {
      this.$refs.duplicateWarningDialog?.close();
      this.categoriaSeleccionada = null;
      this.categoriaObj = null;
      this.pendingCategoryNav = null;
    },

    closeErrorDialog() {
      this.$refs.errorDialog.close();
      // Redirigir a la vista de fincas
      this.setView(1);
    },

    closeQrScanner() {
      this.isQrScannerOpen = false;
    },

    async handleQrScan(rawCode) {
      const scannerRef = this.$refs.qrScannerModal;
      const normalizedCode = String(rawCode || "").trim();

      if (!normalizedCode) {
        scannerRef?.setError("El codigo QR leido no es valido.");
        return;
      }

      scannerRef?.setBusy(true);
      scannerRef?.setError("");

      try {
        const response = await this.$http.get(
          `${this.$hostname}/zones/resolve-qr/?code=${encodeURIComponent(
            normalizedCode,
          )}`,
        );
        const farmland = response?.data?.farmland;
        const zone = response?.data?.zone;

        if (!farmland || !zone) {
          scannerRef?.setError("No se pudo resolver finca y zona desde el QR.");
          return;
        }

        this.applyQrSelection(farmland, zone);
        this.closeQrScanner();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          "No se encontro una zona asociada al QR o no tienes permisos.";
        scannerRef?.setError(message);
      } finally {
        scannerRef?.setBusy(false);
      }
    },

    applyQrSelection(farmland, zone) {
      this.tipoSeleccionado = farmland.farm_type || null;
      this.farmTypeStep = "farmland";
      this.frutaSeleccionada =
        farmland.farm_type === "COOPERATIVE" ? farmland.fruit_type : null;

      this.fincaSeleccionada = farmland;
      this.lomoSeleccionado = null;
      this.variedadSeleccionada = null;
      this.zoneStep = "zone";
      this.zonaSeleccionada = zone;
      this.formatoSeleccionado = null;
      this.modoProduccion = null;
      this.modoProduccionObj = null;
      this.categoriaSeleccionada = null;
      this.categoriaObj = null;
      this.cooperativeStep = "mode";
      this.countWidgetStep = null;

      this.$root.$emit("farmland", farmland);
      this.$root.$emit("zone", zone);
      this.setView(3);
    },
  },
};
</script>

<style scoped>
.view-container {
  padding-top: calc(50px + env(safe-area-inset-top, 0px));
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
}

/* Mobile: breadcrumb puede ocupar hasta 2 filas (~72px) */
.view-container.with-breadcrumb {
  padding-top: calc(122px + env(safe-area-inset-top, 0px));
}

/* Ajuste específico para iOS con header más alto */
@supports (padding: max(0px, env(safe-area-inset-top))) {
  .view-container {
    padding-top: calc(58px + env(safe-area-inset-top, 0px));
  }

  /* Mobile iOS: 58px header + 72px breadcrumb (2 filas) */
  .view-container.with-breadcrumb {
    padding-top: calc(130px + env(safe-area-inset-top, 0px));
  }
}

/* Desktop: breadcrumb es barra única de 56px */
@media (min-width: 768px) {
  .view-container.with-breadcrumb {
    padding-top: calc(106px + env(safe-area-inset-top, 0px));
  }

  @supports (padding: max(0px, env(safe-area-inset-top))) {
    .view-container.with-breadcrumb {
      padding-top: calc(114px + env(safe-area-inset-top, 0px));
    }
  }
}

/* Estilos del diálogo de error */
.error-dialog {
  border: none;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  text-align: center;
}

.error-dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  display: block;
}

.dialog-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 20px 0;
  line-height: 1.4;
}

.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.ok-button {
  padding: 10px 32px;
  background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ok-button:hover {
  background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 118, 49, 0.3);
}

.ok-button:active {
  transform: translateY(0);
}

.duplicate-warning-text {
  font-size: 14px;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 4px 0 16px;
  text-align: center;
  line-height: 1.4;
}

.back-button {
  padding: 10px 32px;
  background: rgba(160, 156, 156, 0.15);
  color: #444;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: rgba(160, 156, 156, 0.28);
  transform: translateY(-1px);
}

.back-button:active {
  transform: translateY(0);
}
</style>
